import { Dispatch, useState } from "react";
import styles from "./CommonFields.module.css";
import { Checkbox, Collapsible, FormLayout, TextField } from "@shopify/polaris";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import { Field } from "@shopify/react-form";
import {
    Action,
    BuyNConfigInterface,
    BxgyConfigInterface,
    FBTConfigInterface,
    MixMatchConfigInterface,
    ProductAddonsConfigInterface,
    ProductAddonsPopupConfigInterface,
    StandardConfigInterface,
    VolumeConfigInterface,
} from "../../Bundles/Types";
import useBundleMixpanelHook from "../../../hooks/useBundleMixpanelHook";
import { BundleTypes } from "../constants";

type Props_BundleInfo = {
    dispatchBundleConfig:
        | Dispatch<Action<StandardConfigInterface>>
        | React.Dispatch<Action<BxgyConfigInterface>>
        | React.Dispatch<Action<MixMatchConfigInterface>>
        | React.Dispatch<Action<VolumeConfigInterface>>
        | React.Dispatch<Action<FBTConfigInterface>>
        | React.Dispatch<Action<ProductAddonsConfigInterface>>
        | React.Dispatch<Action<ProductAddonsPopupConfigInterface>>
        | React.Dispatch<Action<BuyNConfigInterface>>;
    title: Field<string>;
    popup_heading?: Field<string>;
    bundle_name: Field<string>;
    bundleType: string;
    description: Field<string>;
    show_line_around_title?: Field<boolean>;
    show_countdown_timer?: Field<boolean>;
    countdown_timer_minutes?: Field<number>;
    countdown_timer_text?: Field<string>;
    offer_name?: Field<string>;
};

const BundleInfo = ({
    dispatchBundleConfig,
    title,
    popup_heading,
    bundleType,
    show_line_around_title,
    show_countdown_timer,
    countdown_timer_minutes,
    countdown_timer_text,
    offer_name,
}: Props_BundleInfo) => {
    const [expandBundleInfo, setExpandBundleInfo] = useState(true);
    const { trackFormSections } = useBundleMixpanelHook();

    return (
        <>
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandBundleInfo((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Bundle Info ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: bundleType,
                        });
                        return !ps;
                    })
                }
            >
                <div>Bundle Info</div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transform: expandBundleInfo ? "rotate(90deg)" : "rotate(270deg)",
                    }}
                >
                    <MdOutlineArrowBackIosNew size={14} />
                </div>
            </div>
            <Collapsible
                open={expandBundleInfo}
                id=""
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
            >
                <FormLayout>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            ...((bundleType == BundleTypes.MIX_MATCH ||
                                bundleType == BundleTypes.MIX_MATCH_SECTIONAL) && {
                                marginTop: "5px",
                                paddingTop: "10px",
                            }),
                        }}
                    >
                        <TextField
                            label={"Title"}
                            placeholder="Title"
                            autoComplete={"off"}
                            {...title}
                            onBlur={() => title.runValidation()}
                            onChange={(val) => {
                                dispatchBundleConfig({
                                    payload: val,
                                    key: "title",
                                });
                                title.onChange(val);
                            }}
                            maxLength={80}
                            showCharacterCount
                        />
                        {offer_name && (
                            <TextField
                                label={"Offer Name"}
                                placeholder="Offer name"
                                autoComplete={"off"}
                                helpText={"This name is only visible to you. It helps you identify the bundle."}
                                {...offer_name}
                                onBlur={() => offer_name.runValidation()}
                                onChange={(val) => {
                                    dispatchBundleConfig({
                                        payload: val,
                                        key: "offer_name",
                                    });
                                    offer_name.onChange(val);
                                }}
                                maxLength={80}
                                showCharacterCount
                            />
                        )}
                        {bundleType === BundleTypes.PRODUCT_ADDONS_POPUP && popup_heading && (
                            <TextField
                                label={"Popup Heading"}
                                placeholder="Special offer for you"
                                autoComplete={"off"}
                                {...popup_heading}
                                onBlur={() => popup_heading.runValidation()}
                                onChange={(val) => {
                                    dispatchBundleConfig({
                                        payload: val,
                                        key: "popup_heading" as any,
                                    });
                                    popup_heading.onChange(val);
                                }}
                                maxLength={80}
                                showCharacterCount
                            />
                        )}
                        {bundleType === BundleTypes.VOLUME_DISCOUNT && show_line_around_title && (
                            <Checkbox
                                label={"Show line around title"}
                                {...show_line_around_title}
                                value=""
                                checked={show_line_around_title.value}
                                onChange={(val) => {
                                    dispatchBundleConfig({
                                        payload: val,
                                        key: "show_line_around_title" as any,
                                    });
                                    show_line_around_title.onChange(val);
                                }}
                            />
                        )}

                        {bundleType === BundleTypes.VOLUME_DISCOUNT && show_countdown_timer && (
                            <>
                                <Checkbox
                                    label={"Show countdown timer"}
                                    {...show_countdown_timer}
                                    value=""
                                    checked={show_countdown_timer.value}
                                    onChange={(val) => {
                                        dispatchBundleConfig({
                                            payload: val,
                                            key: "show_countdown_timer" as any,
                                        });
                                        show_countdown_timer.onChange(val);
                                    }}
                                />

                                {show_countdown_timer.value && (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "10px",
                                            padding: "10px",
                                            borderRadius: "8px",
                                            backgroundColor: "#f9f9f9",
                                        }}
                                    >
                                        {countdown_timer_text && (
                                            <TextField
                                                label={"Timer Text"}
                                                placeholder="The offer is going to expire soon in {timer}, hurry up!"
                                                autoComplete={"off"}
                                                {...countdown_timer_text}
                                                onChange={(val) => {
                                                    dispatchBundleConfig({
                                                        payload: val,
                                                        key: "countdown_timer_text" as any,
                                                    });
                                                    countdown_timer_text.onChange(val);
                                                }}
                                                helpText={"Use {timer} to show countdown timer"}
                                            />
                                        )}

                                        {countdown_timer_minutes && (
                                            <div className={styles.formRow}>
                                                <TextField
                                                    label={"Timer Minutes"}
                                                    placeholder="5"
                                                    autoComplete={"off"}
                                                    value={countdown_timer_minutes.value.toString()}
                                                    min={1}
                                                    type="integer"
                                                    onChange={(val) => {
                                                        dispatchBundleConfig({
                                                            payload: +val,
                                                            key: "countdown_timer_minutes" as any,
                                                        });
                                                        countdown_timer_minutes.onChange(+val);
                                                    }}
                                                    onBlur={(e) => {
                                                        const input = e?.target as HTMLInputElement;
                                                        const value = Number(input?.value);
                                                        if (isNaN(value) || value < 1) {
                                                            dispatchBundleConfig({
                                                                payload: 1,
                                                                key: "countdown_timer_minutes" as any,
                                                            });
                                                            countdown_timer_minutes.onChange(1);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    {/* <div className={`${styles.formRow} ${styles.row2}`}>
                        <TextField
                            label={"Description"}
                            placeholder="Description..."
                            autoComplete="off"
                            multiline={2}
                            {...description}
                            onChange={(val) => {
                                dispatchBundleConfig({
                                    payload: val,
                                    key: "description",
                                });
                                description.onChange(val);
                            }}
                        />
                    </div> */}
                </FormLayout>
            </Collapsible>
        </>
    );
};

export default BundleInfo;
